import { Button, TextField } from '@ads/components-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CitySearchBarProps {
  /** Texto de busca já aplicado (usado para inicializar o campo). */
  readonly defaultValue: string;
  /** Acionado ao pesquisar (Enter ou botão), com o texto atual. */
  readonly onSearch: (query: string) => void;
  /** Acionado ao limpar o filtro de busca. */
  readonly onClear: () => void;
}

/**
 * Barra de busca da listagem de Cidades. Mantém o texto pendente localmente e só
 * aplica ao acionar "Pesquisar" (ou Enter), acionando `onSearch`. "Limpar" zera o
 * campo e o filtro aplicado via `onClear`.
 *
 * TODO: a busca é **client-side** (filtra a lista completa já carregada). Quando
 * o backend implementar busca em `GET /api/v1/location/cities` (ex.: `?search=`),
 * enviar o termo via `useCityList` e remover a filtragem client-side na página.
 */
export function CitySearchBar({ defaultValue, onSearch, onClear }: CitySearchBarProps) {
  const { t } = useTranslation('location');
  const [pending, setPending] = useState(defaultValue);

  const handleSearch = () => onSearch(pending.trim());

  const handleClear = () => {
    setPending('');
    onClear();
  };

  return (
    <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1">
      <form
        className="flex flex-col items-start gap-nano"
        onSubmit={e => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <TextField
          label={t('cities.search.label')}
          placeholder={t('cities.search.placeholder')}
          value={pending}
          size="md"
          fullWidth
          leadingIcon={MagnifyingGlassIcon}
          onChange={e => setPending(e.target.value)}
        />
        <div className="flex items-center gap-nano">
          <Button type="submit" hierarchy="primary" size="md" leadingIcon={MagnifyingGlassIcon}>
            {t('cities.search.button')}
          </Button>
          <Button type="button" hierarchy="secondary" size="md" onClick={handleClear}>
            {t('cities.search.clear')}
          </Button>
        </div>
      </form>
    </div>
  );
}
